import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Edit01, Trash01 } from "@vctors/vctors-untitled-ui/icons";
import {
  ButtonUtility,
  LoadingIndicator,
  PaginationPageMinimalCenter,
  Table,
  TableCard,
} from "@vctors/vctors-untitled-ui";
import type { SortDescriptor } from "react-aria-components";
import { formatDateWithMoment } from "@surgia/utils";
import {
  handlePaginationChange,
  handleSort,
  parseSortDescriptor,
} from "../../utils/table";
import { GenericTableHeader } from "@surgia/ui";
import Badge from "@surgia/ui/components/badge";

interface IPurchaseOrder {
  id: number;
  orderedBy: string;
  branch: string;
  totalQuantity: number;
  "orderSummary.total": number;

  createdAt: string;
  status: string;
  branchId: number;
  numberOfPurchaseRequests: number;
}

interface IPurchaseOrderTableProps {
  data: IPurchaseOrder[];
  loading?: boolean;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  showBranchColumn?: boolean;
  selectedPurchaseOrders: string[];
  setSelectedPurchaseOrders: (
    orders: string[] | ((prev: string[]) => string[])
  ) => void;
  status: string | undefined;
  setStatus: (status: string) => void;
  onViewDetails: (poId: string, branchId: string) => void;
  onEdit?: (poId: string) => void;
  onDelete?: (poId: string) => void;
  onApprove?: (poId: string) => void;
  onReject?: (poId: string) => void;
  // Add branches dropdown props
  branches?: any[];
  selectedBranch?: string | null;
  setSelectedBranch?: (branch: string | null) => void;
  allUserBranches?: string;
  showBranchesDropDown?: boolean;
  showAllBranches?: boolean;
  isBranchesLoading?: boolean;
  totalCount?: number;
}

export function PurchaseOrderTableUntitled({
  data,
  loading,
  currentPage = 0,
  totalPages = 1,
  onPageChange,
  showBranchColumn = true,
  onViewDetails,
  onEdit,
  onDelete,
  branches,
  selectedBranch,
  setSelectedBranch,
  allUserBranches,
  showBranchesDropDown = false,
  showAllBranches = true,
  isBranchesLoading = false,
  totalCount,
}: IPurchaseOrderTableProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get sort parameter from URL
  const sortParam = searchParams.get("sort");
  const pageParam = searchParams.get("page");

  // sortDescriptor is used by the Table component for UI visual indicators
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>(
    parseSortDescriptor(sortParam || undefined, "id", "descending")
  );

  // Sync sortDescriptor with URL changes to keep UI indicators accurate
  useEffect(() => {
    setSortDescriptor(
      parseSortDescriptor(sortParam || undefined, "id", "descending")
    );
  }, [sortParam]);

  const onSortChange = (descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor);
    handleSort(
      navigate,
      descriptor.column as string,
      descriptor.direction as string
    );
  };

  const onPageChangeHandler = (page: number) => {
    if (onPageChange) {
      onPageChange(page - 1);
    } else {
      handlePaginationChange(navigate, page - 1);
    }
  };
  const capitalizeFirstChar = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Determine if branch column should be shown
  // Hide branch column when a specific branch is selected (not "All Branches")
  const shouldShowBranchColumn =
    showBranchColumn && (selectedBranch === allUserBranches || !selectedBranch);

  // Calculate total columns for colSpan
  const hasActionsColumn = !!(onEdit || onDelete);
  const totalColumns =
    7 + (shouldShowBranchColumn ? 1 : 0) + (hasActionsColumn ? 1 : 0);

  return (
    <TableCard.Root className="bg-white !ring-[#E9EAEB] !ring-1 !shadow-none !border-none">
      <TableCard.Header
        className="bg-white"
        title=""
        badge=""
        contentTrailing={
          <GenericTableHeader
            title="Purchase Orders"
            totalCount={totalCount || 0}
            showBadge={true}
            customBadgeText={`${totalCount || 0}`}
            showBorder={false}
            padding=""
            layout="justified"
            className="w-full"
            showBranchSelector={showBranchesDropDown}
            branches={branches || []}
            selectedBranch={selectedBranch}
            setSelectedBranch={(branch) => setSelectedBranch?.(branch || "")}
            isBranchesLoading={isBranchesLoading}
            showAllBranches={showAllBranches}
            branchPlaceholder="All Branches"
          />
        }
      />

      <Table
        aria-label="Purchase Orders"
        selectionMode="none"
        sortDescriptor={sortDescriptor}
        onSortChange={onSortChange}
        className="bg-white"
      >
        <Table.Header className="bg-[#FAFAFA] text-[#717680]">
          <Table.Head id="id" allowsSorting label="ID" isRowHeader></Table.Head>
          <Table.Head id="orderedBy" label="Ordered By" allowsSorting />
          {shouldShowBranchColumn && (
            <Table.Head id="branch" label="Shipping To" allowsSorting />
          )}
          <Table.Head
            id="numberOfPurchaseRequests"
            label="No. PRs"
            allowsSorting
          />
          <Table.Head id="totalQuantity" label="No. Products" allowsSorting />
          <Table.Head id="orderTotal" label="Total price (RS)" allowsSorting />
          <Table.Head id="status" label="Status" allowsSorting />

          <Table.Head id="createdAt" label="Date created" allowsSorting />
          {(onEdit || onDelete) && <Table.Head id="actions" />}
        </Table.Header>

        <Table.Body>
          {loading ? (
            <Table.Row className="border-t border-[#E9EAEB]">
              <Table.Cell colSpan={totalColumns}>
                <div className="flex h-64 items-center justify-center">
                  <LoadingIndicator type="dot-circle" size="md" />
                </div>
              </Table.Cell>
            </Table.Row>
          ) : data?.length > 0 ? (
            data.map((item: IPurchaseOrder) => (
              <Table.Row
                key={item.id}
                id={item.id.toString()}
                className="border-t border-[#E9EAEB] hover:bg-gray-50"
              >
                <Table.Cell>
                  <div className="relative group">
                    <button
                      onClick={() =>
                        onViewDetails(
                          item.id.toString(),
                          item.branchId.toString()
                        )
                      }
                      className="cursor-pointer text-sm  text-purple-700 hover:text-purple-700/80 transition-colors"
                    >
                      {item.id}
                    </button>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 min-w-max">
                      {`View ${item.id}`}
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 relative group">
                      <p className="text-sm font-medium text-primary break-words truncate max-w-[150px]">
                        {item.orderedBy}
                      </p>
                      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 min-w-max">
                        {item.orderedBy}
                      </div>
                    </div>
                  </div>
                </Table.Cell>
                {shouldShowBranchColumn && (
                  <Table.Cell>
                    <Badge variant="default">{item.branch}</Badge>
                  </Table.Cell>
                )}
                <Table.Cell>
                  <span className="text-sm text-gray-600">
                    {item.numberOfPurchaseRequests}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-gray-600">
                    {item.totalQuantity}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-gray-600">
                    {item["orderSummary.total"]
                      ? `${item["orderSummary.total"]}`
                      : "-"}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <Badge variant="default" className="!text-xs">
                    {capitalizeFirstChar(item.status)}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-gray-600">
                    {item.createdAt
                      ? formatDateWithMoment(item.createdAt)
                      : "-"}
                  </span>
                </Table.Cell>

                {(onEdit || onDelete) && (
                  <Table.Cell className="px-4">
                    <div className="flex justify-end gap-1">
                      {onEdit && (
                        <ButtonUtility
                          size="xs"
                          color="tertiary"
                          tooltip="Edit"
                          icon={Edit01}
                          onClick={() => onEdit(item.id.toString())}
                          data-testid="edit-po-button"
                        />
                      )}

                      {onDelete && (
                        <ButtonUtility
                          size="xs"
                          color="tertiary"
                          tooltip="Delete"
                          icon={Trash01}
                          onClick={() => onDelete(item.id.toString())}
                          data-testid="delete-po-button"
                        />
                      )}
                    </div>
                  </Table.Cell>
                )}
              </Table.Row>
            ))
          ) : (
            <Table.Row className="border-t border-[#E9EAEB]">
              <Table.Cell colSpan={totalColumns}>
                <div className="flex h-64 items-center justify-center">
                  <p className="text-gray-500 text-center">
                    No purchase orders found
                  </p>
                </div>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>

      <div className="[&_*]:!border-gray-200 [&_*]:!ring-gray-200 [&_button]:!bg-white [&_button]:!text-gray-700 [&_button[aria-current='true']]:!bg-gray-200 [&_button[aria-current='true']]:!text-gray-900">
        <PaginationPageMinimalCenter
          page={(parseInt(pageParam || "0") || currentPage) + 1}
          total={totalPages}
          onPageChange={onPageChangeHandler}
          className="px-4 py-3 md:px-6 md:pt-3 md:pb-4"
        />
      </div>
    </TableCard.Root>
  );
}
